import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  HostBinding,
  HostListener,
  inject,
  input,
  Output,
  ViewChild,
  computed,
  effect,
  signal,
  DOCUMENT,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import {
  BookingAvailabilityOption,
  ConversationMessage,
} from '../../../../core/src/lib/interface/conversation.interface';
import { MessageType } from '@vendasta/conversation';
import { ChatContainerComponent, ChatMessageStatus, GalaxyChatModule } from '@vendasta/galaxy/chat';
import { ChatComposerComponent, GalaxyChatComposerModule } from '@vendasta/galaxy/chat-composer';
import { SentMessage } from '@vendasta/galaxy/chat-composer/src/chat-composer.component';
import { GlxyMarkdownPipe, LinkyPipe } from '@vendasta/galaxy/pipes';
import { MessageGroup } from '../../models/message-group';
import { DefaultPosition } from '../../models/webchat-config';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { BookingAvailabilityComponent, ConversationPipesModule } from '../../../../ui/src';
import { isAIFallbackKey, mapMessageStatus } from '../../../../core/src/lib/message-utils';
import { WebchatStatusComponent } from '../webchat-status/webchat-status.component';
import { SentFromYouPipe } from '../../pipes/send-from-you.pipe';
import { toObservable } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TEMP_AI_ASSISTANT_PARTICIPANT_ID, TEMP_PARTICIPANT_ID } from '../../util/constants';
import { MatTooltipModule } from '@angular/material/tooltip';

const defaultAssistantName = 'Chat Receptionist';
const WEBCHAT_GREETING_KEY = 'INBOX.WEBCHAT.SETTINGS.WEBCHAT_CUSTOM_GREETING_PLACEHOLDER';

@Component({
  selector: 'webchat-box',
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatInputModule,
    MatTooltipModule,
    GalaxyChatModule,
    GalaxyChatComposerModule,
    LinkyPipe,
    GlxyMarkdownPipe,
    GalaxyAvatarModule,
    BookingAvailabilityComponent,
    ConversationPipesModule,
    WebchatStatusComponent,
    SentFromYouPipe,
    TranslateModule,
  ],
  templateUrl: './webchat-box.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./webchat-box.component.scss'],
})
export class WebchatBoxComponent {
  @HostBinding('class') readonly hostClass = 'chat-box';
  @HostBinding('attr.role') readonly role = 'dialog';
  @HostBinding('attr.aria-labelledby') readonly ariaLabelledby = 'webchat-title';
  @HostBinding('attr.tabindex') readonly tabindex = '-1';

  webchatColor = input<string>('');
  webchatTextColor = input<string>('');
  webchatAccentColor = input<string>('');
  webchatAccentTextColor = input<string>('');
  welcomeMessage = input<string>('');
  assistantName = input(defaultAssistantName, {
    transform: (value: string | undefined) => value || defaultAssistantName,
  });
  assistantAvatarUrl = input<string>('');
  currentParticipantId = input.required<string | null>();
  messageGroups = input.required<MessageGroup[]>();
  messages = input.required<ConversationMessage[]>();
  messages$ = toObservable(this.messages);
  fullView = input<boolean>(false);
  position = input<'right' | 'left'>(DefaultPosition);
  absolutePosition = input<boolean>(true);
  isReplying = input(false);
  placeholder = input<string>('');
  showFooterContent = input<boolean>(false);
  poweredBy = input<{ name: string; url: string } | null>(null);
  processing = input<boolean>(false);
  canClose = input<boolean>(true);
  embedded = input<boolean>(false);
  hideEmptyState = input<boolean>(false);
  preventScrollOnFocus = input<boolean>(false);
  hideComposer = input<boolean>(false);

  @Output() clickedClose = new EventEmitter<void>();
  @Output() clickedHeader = new EventEmitter<void>();
  @Output() sentMessage = new EventEmitter<SentMessage>();
  @Output() setPreferredBooking = new EventEmitter<BookingAvailabilityOption>();

  @HostBinding('class.full-view') get fullViewStyles() {
    return this.fullView();
  }

  @HostBinding('class.mini-view') get miniViewStyles() {
    return !this.fullView();
  }

  @HostBinding('class.chat-box--right') get rightStyles() {
    return this.position() === 'right';
  }

  @HostBinding('class.chat-box--left') get leftStyles() {
    return this.position() === 'left';
  }

  @HostBinding('class.chat-box--not-embedded') get notEmbeddedStyles() {
    return !this.embedded();
  }

  @HostBinding('style.position') get positionStyles() {
    return this.absolutePosition() ? 'absolute' : 'relative';
  }

  @HostBinding('attr.aria-modal') get ariaModal(): string | null {
    return this.fullView() ? 'true' : null;
  }

  @HostBinding('style.--primary-color') get stylePrimaryColor() {
    return this.webchatColor();
  }

  @HostBinding('style.--text-color') get styleTextColor() {
    if (this.webchatTextColor() === 'light') return 'white';
    return 'black';
  }

  @HostBinding('style.--custom-chat-bubble-color')
  @HostBinding('style.--custom-chat-button-color')
  get styleChatColor() {
    return this.webchatAccentColor();
  }

  @HostBinding('style.--custom-chat-bubble-text-color')
  @HostBinding('style.--custom-chat-button-text-color')
  get styleChatTextColor() {
    return this.webchatAccentTextColor() === 'dark' ? 'black' : 'white';
  }

  @ViewChild('chatContainer') chatContainer?: ChatContainerComponent;
  @ViewChild('chatComposer') chatComposer?: ChatComposerComponent;

  readonly parentHostname = input('');
  maxMessageLength = 1000;

  // Live announcements for screen readers
  private readonly liveAnnouncementSignal = signal<string>('');
  private lastAnnouncedMessageCount = 0;
  readonly liveAnnouncement = computed(() => this.liveAnnouncementSignal());
  private readonly document = inject(DOCUMENT);
  private readonly translate = inject(TranslateService);
  readonly currentHostName = computed(() => this.parentHostname() || this.document.location.hostname);

  readonly aiParticipantId = computed(() => {
    const currentParticipantId = this.currentParticipantId();
    const msgs = this.messages();
    for (let i = msgs.length - 1; i >= 0; i--) {
      const senderParticipantId = msgs[i].sender?.participantId;
      if (
        senderParticipantId &&
        senderParticipantId !== currentParticipantId &&
        senderParticipantId !== TEMP_AI_ASSISTANT_PARTICIPANT_ID &&
        senderParticipantId !== TEMP_PARTICIPANT_ID
      ) {
        return senderParticipantId;
      }
    }
    return null;
  });

  constructor() {
    // Auto focus when opening in full view (modal mode)
    effect(() => {
      if (this.fullView()) {
        setTimeout(() => this.chatComposer?.focusTextArea({ preventScroll: this.preventScrollOnFocus() }), 0);
      }
      // Re-trigger resize after ShadowDom styles have settled (applies to both mini and full view)
      setTimeout(() => requestAnimationFrame(() => this.chatComposer?.resizeTextArea()), 0);
    });

    // Announce new messages
    effect(() => {
      const messages = this.messages();
      if (messages && messages.length > 0) {
        // Find new messages since last announcement
        const newMessages = messages.slice(0, messages.length - this.lastAnnouncedMessageCount);
        const assistantMessages = newMessages.filter((message) => {
          const senderParticipantId = message.sender?.participantId || null;
          return (
            senderParticipantId &&
            senderParticipantId !== this.currentParticipantId() &&
            senderParticipantId !== TEMP_PARTICIPANT_ID
          );
        });

        if (assistantMessages.length > 0) {
          const announcements = assistantMessages.map((message) => {
            const aiParticipantId = this.aiParticipantId();
            const senderName =
              !aiParticipantId ||
              message.sender?.participantId === aiParticipantId ||
              message.sender?.participantId === TEMP_AI_ASSISTANT_PARTICIPANT_ID
                ? this.assistantName()
                : message.sender?.name || this.translate.instant('INBOX.WEBCHAT.WIDGET.SUPPORT_AGENT');
            return `${senderName}: ${message.body || ''}`;
          });
          const fullAnnouncement = announcements.join('. ');

          if (fullAnnouncement && this.liveAnnouncementSignal() !== fullAnnouncement) {
            this.liveAnnouncementSignal.set(fullAnnouncement);
            this.chatContainer?.onNewMessageReceived();
          }
        }

        this.lastAnnouncedMessageCount = messages.length;
      }
    });
  }

  protected async sendMessage(msg: SentMessage): Promise<void> {
    this.sentMessage.emit(msg);
    this.chatContainer?.onNewMessageSent();
    setTimeout(() => this.chatComposer?.resizeTextArea(), 0);
  }

  public onNewMessageSent(): void {
    this.chatContainer?.onNewMessageSent();
  }

  protected onClickClose(): void {
    this.clickedClose.emit();
  }

  protected onClickHeader(): void {
    this.clickedHeader.emit();
  }

  trackById(_: number, item: ConversationMessage | MessageGroup): string {
    if ('messageId' in item) return item.messageId || '';
    return item.id || '';
  }

  handleInput(event: KeyboardEvent): void {
    event.stopPropagation();
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    // Only handle Escape when in full view (modal mode)
    if (event.key === 'Escape' && this.fullView()) {
      event.preventDefault();
      event.stopPropagation();
      this.clickedClose.emit();
    }
  }

  //WARP-605 - Preserve scroll on wheel event for Divi theme and websitepro
  @HostListener('wheel', ['$event'])
  onWheel(event: WheelEvent) {
    event.stopPropagation();
  }

  mapMessageStatus(message: ConversationMessage): ChatMessageStatus {
    return mapMessageStatus(message);
  }

  shouldTranslateBody(body: string | null | undefined): boolean {
    return body === WEBCHAT_GREETING_KEY || isAIFallbackKey(body);
  }

  async handleSetPreferredBooking(booking: BookingAvailabilityOption) {
    this.setPreferredBooking.emit(booking);
  }

  protected readonly MessageType = MessageType;

  public focusInput(options?: FocusOptions): void {
    this.chatComposer?.focusTextArea(options);
  }

  public insertMessage(message: string, position: 'before' | 'after' | 'curser' = 'curser'): void {
    this.chatComposer?.insertDynamicContent(message, position);
  }

  senderNameForGroup(group: MessageGroup): string {
    if (group.participantId === TEMP_AI_ASSISTANT_PARTICIPANT_ID) {
      return this.assistantName();
    }
    const aiParticipantId = this.aiParticipantId();
    if (!aiParticipantId || group.participantId === aiParticipantId) {
      return this.assistantName();
    }
    return group.lastMessage?.sender?.name || this.translate.instant('INBOX.WEBCHAT.WIDGET.SUPPORT_AGENT');
  }
}
